import React, { useEffect } from 'react';
import { Button, Menu, MenuItem, ListItemIcon } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import i18n from '@utils/i18next';
import { LANGUAGES } from '@constants/constants';

interface LanguageSelectorProps {
  anchorEl: null | HTMLElement;
  setAnchorEl: React.Dispatch<React.SetStateAction<null | HTMLElement>>;
  isMobile: boolean;
  isModal?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  anchorEl,
  setAnchorEl,
  isMobile,
  isModal = false,
}) => {
  const open = Boolean(anchorEl);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocale = urlParams.get('locale');

    if (urlLocale && LANGUAGES.map((l) => l.locale).includes(urlLocale)) {
      i18n.changeLanguage(urlLocale);
      localStorage.setItem('app_locale', urlLocale);
    } else {
      const storedLocale = localStorage.getItem('app_locale');
      const localeToUse =
        storedLocale && LANGUAGES.map((l) => l.locale).includes(storedLocale) ? storedLocale : 'en';

      if (localeToUse !== i18n.language) {
        i18n.changeLanguage(localeToUse);
        localStorage.setItem('app_locale', localeToUse);
      }
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (selectedLocale: string) => {
    i18n.changeLanguage(selectedLocale);
    localStorage.setItem('app_locale', selectedLocale);
    handleClose();
  };

  const currentLang = LANGUAGES.find((l) => l.locale === i18n.language)?.label || 'EN';

  return (
    <>
      <Button
        disableTouchRipple
        disableElevation
        disableRipple
        disableFocusRipple
        variant="outlined"
        startIcon={<TranslateIcon sx={{ ...(isModal && { color: '#2F736E' }) }} />}
        endIcon={<NavigateNextIcon sx={{ color: '#101112' }} />}
        onClick={handleClick}
        sx={{
          textTransform: 'none',
          color: '#101112',
          fontWeight: 550,
          fontSize: '1rem',
          lineHeight: '1.25rem',
          width: isMobile ? '60px' : '160px',
          borderRadius: '0.875rem',
          px: isMobile ? '2rem' : '0.5rem',
          py: '0.5rem',
          minWidth: isMobile ? 'auto' : undefined,
          justifyContent: 'center',
          ...(isModal && {
            border: open ? '1px solid rgba(16, 17, 18, 0.5)' : '1px solid transparent',
            '&:hover': {
              border: '1px solid rgba(16, 17, 18, 0.5)',
              backgroundColor: 'transparent',
            },
          }),
        }}
      >
        {!isMobile && currentLang}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: isModal ? 'center' : 'bottom', horizontal: 'left' }}
        sx={{
          // Dialog is at 9995 to sit above FullPageLoader (9988); menu must be higher than 9995
          ...(isModal && { zIndex: 9999 }),
        }}
        PaperProps={{
          sx: {
            width: 200,
            borderRadius: '0.875rem',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
            padding: '0.625rem',
          },
        }}
        MenuListProps={{
          sx: { padding: 0 },
        }}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.locale}
            onClick={() => handleSelect(lang.locale)}
            sx={{
              width: '100%',
              fontWeight: 550,
              fontSize: '1rem',
              color: '#162936',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '.875rem 0.75rem ',
              backgroundColor: '#F6F8F9',
              borderRadius: '0.875rem',
              '&:not(:last-child)': {
                marginBottom: '0.5rem',
              },
            }}
          >
            {lang.label}
            {i18n.language === lang.locale && (
              <ListItemIcon>
                <CheckCircleIcon sx={{ fontSize: '1.25rem', color: '#51B8B0' }} />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
